import { useMemo, useState } from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { format, parseISO } from 'date-fns'
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card'
import { ChartSkeleton } from '../ui/Skeleton'
import type { DailyRevenueItem } from '../../lib/api'
import { shibesToDoge } from '../../lib/utils'

interface DailyChartProps {
  data: DailyRevenueItem[] | null
  loading: boolean
  dateRange: number
  onDateRangeChange: (days: number) => void
}

export default function DailyChart({
  data,
  loading,
  dateRange,
  onDateRangeChange,
}: DailyChartProps) {
  const [showCampaign, setShowCampaign] = useState(true)
  const [showSurvey, setShowSurvey] = useState(true)

  const chartData = useMemo(() => {
    if (!data) return []

    // Group by date
    const groupedByDate = data.reduce<Record<string, {
      date: string
      campaign_fee: number
      survey_commission: number
      total: number
    }>>((acc, item) => {
      if (!acc[item.date]) {
        acc[item.date] = {
          date: item.date,
          campaign_fee: 0,
          survey_commission: 0,
          total: 0,
        }
      }

      const doge = shibesToDoge(item.total_amount)
      acc[item.date][item.revenue_type as 'campaign_fee' | 'survey_commission'] = doge
      acc[item.date].total += doge

      return acc
    }, {})

    // Convert to array and sort by date
    return Object.values(groupedByDate).sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    )
  }, [data])

  if (loading) {
    return <ChartSkeleton />
  }

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <CardTitle>Daily Revenue</CardTitle>
        <div className="flex flex-wrap items-center gap-3">
          {/* Toggle filters */}
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={showCampaign}
                onChange={(e) => setShowCampaign(e.target.checked)}
                className="rounded border-slate-600 bg-slate-700 text-doge-500 focus:ring-doge-500"
              />
              <span className="text-xs text-blue-400">Campaign</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={showSurvey}
                onChange={(e) => setShowSurvey(e.target.checked)}
                className="rounded border-slate-600 bg-slate-700 text-doge-500 focus:ring-doge-500"
              />
              <span className="text-xs text-green-400">Survey</span>
            </label>
          </div>

          {/* Date range selector */}
          <div className="flex bg-slate-700 rounded-lg p-1">
            {[7, 30, 90].map((days) => (
              <button
                key={days}
                onClick={() => onDateRangeChange(days)}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  dateRange === days
                    ? 'bg-doge-500 text-slate-900'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                {days}D
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <div className="h-48 sm:h-64 flex items-center justify-center text-slate-500">
            No revenue data for this period
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%" className="min-h-[250px] sm:min-h-[300px]">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => format(parseISO(value), 'MMM d')}
                stroke="#64748b"
                fontSize={12}
              />
              <YAxis
                tickFormatter={(value) => `${value.toFixed(0)}`}
                stroke="#64748b"
                fontSize={12}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1e293b',
                  border: '1px solid #334155',
                  borderRadius: '8px',
                }}
                labelFormatter={(value) => format(parseISO(value as string), 'MMM d, yyyy')}
                formatter={(value: number, name: string) => [
                  `${value.toFixed(2)} DOGE`,
                  name === 'campaign_fee'
                    ? 'Campaign Fees'
                    : name === 'survey_commission'
                    ? 'Survey Commissions'
                    : 'Total',
                ]}
              />
              <Legend
                formatter={(value) =>
                  value === 'campaign_fee'
                    ? 'Campaign Fees'
                    : value === 'survey_commission'
                    ? 'Survey Commissions'
                    : 'Total'
                }
              />
              {showCampaign && (
                <Line
                  type="monotone"
                  dataKey="campaign_fee"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              )}
              {showSurvey && (
                <Line
                  type="monotone"
                  dataKey="survey_commission"
                  stroke="#22c55e"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              )}
              <Line
                type="monotone"
                dataKey="total"
                stroke="#f59e0b"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}
